import { ReactNode, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';

import { useBreakpoints, useIsSmallSizeThemeVariant } from '@/hooks';

interface BaseSelectFilterProps {
  id: string;
  label: string;
  values: string[];
  loading?: boolean;
  error?: string | null;
  onClear: () => void;
  onChange: (value: string[]) => void;
  onRetry?: () => void;
  renderChips: (value: string[]) => ReactNode;
  maxHeight?: string | number;
  children: ReactNode;
}

function BaseSelectFilter(props: BaseSelectFilterProps) {
  const {
    id,
    label,
    values,
    loading,
    error,
    renderChips,
    children,
    onClear,
    onChange,
    onRetry,
    maxHeight,
  } = props;

  const { isMobileDevice } = useBreakpoints();
  const isSmallSizeThemeVariant = useIsSmallSizeThemeVariant();

  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!open);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpenOnClick = () => {
    if (!open) {
      setOpen(true);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    // dont propagate event to avoid re-opening menu
    event.preventDefault();
    event.stopPropagation();
    onClear();
  };

  const handleChange = (event: SelectChangeEvent<string[]>) =>
    onChange(event.target.value as string[]);

  const handleRetry = () => {
    onRetry && onRetry();
  };

  return (
    <FormControl sx={{ width: '100%' }} onClick={handleOpenOnClick}>
      <InputLabel id={`${id}-label`} error={Boolean(error)}>
        {label}
      </InputLabel>
      <Select
        id={id}
        labelId={`${id}-label`}
        label={label}
        multiple
        open={open && !loading && !error}
        onClose={handleClose}
        onChange={handleChange}
        value={values}
        autoWidth={false}
        renderValue={(selected) => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 0.5,
              flexDirection: 'row',
              alignItems: 'center',
              overflow: 'auto',
              height: '100%',
            }}
          >
            {renderChips(selected)}
          </Box>
        )}
        sx={{
          paddingRight: 0,
          '& .MuiSelect-icon': {
            display: 'none',
          },
        }}
        inputProps={{
          sx: {
            height: `${isSmallSizeThemeVariant ? 40 : 56}px !important`,
            paddingLeft: '8.5px',
            paddingRight: '0px !important',
            paddingTop: 0,
            paddingBottom: 0,
          },
        }}
        input={
          <OutlinedInput
            label={label}
            error={Boolean(error)}
            endAdornment={
              <InputAdornment position="end" sx={{ marginLeft: 0 }}>
                {loading && (
                  <CircularProgress
                    size={isSmallSizeThemeVariant ? 24 : 32}
                    sx={{ mr: 1 }}
                  />
                )}
                {error && typeof onRetry !== 'undefined' && (
                  <Tooltip title="Retry">
                    <IconButton sx={{ mr: '8px' }} onClick={handleRetry}>
                      <ReplayIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!loading && !error && (
                  <>
                    {values.length > 0 && (
                      <IconButton onClick={handleClear}>
                        <CloseIcon />
                      </IconButton>
                    )}
                    <IconButton sx={{ mr: '4px' }} onClick={toggleOpen}>
                      <ArrowDropDownIcon
                        sx={{
                          transform: open ? 'rotate(-180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </IconButton>
                  </>
                )}
              </InputAdornment>
            }
          />
        }
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight:
                typeof maxHeight !== 'undefined' ? maxHeight : 48 * 5.5 + 8,
              ...(isMobileDevice ? { left: '8px !important' } : {}),
            },
          },
        }}
      >
        {children}
      </Select>
      {error && (
        <FormHelperText
          sx={(theme) => ({
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            marginLeft: 0,
          })}
        >
          <ErrorOutlineIcon fontSize="small" />
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export { BaseSelectFilter };
